import { Box, Tooltip, Typography } from "@mui/material";
import { formatNumber } from "../format";
import { EmissionTrendPoint, niceMax } from "./borrowerSummary";

/**
 * Financial Emission Trend body — Figma "Chart/Loan_Lifecycle" 7475:15212.
 *
 * Fixed Figma geometry (Details 7475:15228):
 *   unit row 12px, 16px gap, then 5 gridline rows 12px tall / 27px apart, so the
 *   plot spans 4 × 39 = 156px. Columns (80px, bar 48px) sit in a 225px-tall
 *   strip starting 57px in, 12px above their 24px year label, so the zero line
 *   is 189px down. The line is intensity on its own scale across the same 156px.
 */
const TICKS = 5;
const STEPS = TICKS - 1;
const ROW = 12;
const ROW_GAP = 27;
const PLOT = STEPS * (ROW + ROW_GAP); // 156
const LABEL_WIDTH = 36;
const LABEL_GAP = 6;
const BARS_LEFT = 57;
const COLUMN = 80;
const BAR = 48;
const STRIP = 225;
const X_LABEL = 24;
const X_LABEL_GAP = 12;
const ZERO = STRIP - X_LABEL - X_LABEL_GAP; // 189
const DOT = 12;

interface Props {
  points: EmissionTrendPoint[];
  /** Axis caption, e.g. "tCO₂e". */
  unit: string;
  barLabel: string;
  lineLabel: string;
  formatIntensity: (n: number) => string;
}

interface XY {
  x: number;
  y: number;
}

/** Smooth path through the points (Catmull-Rom → cubic Bézier), like Figma's curve. */
const smoothPath = (pts: XY[]): string => {
  if (pts.length === 1) return `M${pts[0].x},${pts[0].y}`;
  return pts.reduce((d, p, i) => {
    if (i === 0) return `M${p.x},${p.y}`;
    const p0 = pts[i - 2] ?? pts[i - 1];
    const p1 = pts[i - 1];
    const p3 = pts[i + 1] ?? p;
    const c1 = { x: p1.x + (p.x - p0.x) / 6, y: p1.y + (p.y - p0.y) / 6 };
    const c2 = { x: p.x - (p3.x - p1.x) / 6, y: p.y - (p3.y - p1.y) / 6 };
    return `${d} C${c1.x},${c1.y} ${c2.x},${c2.y} ${p.x},${p.y}`;
  }, "");
};

const EmissionTrendChart = ({
  points,
  unit,
  barLabel,
  lineLabel,
  formatIntensity,
}: Props) => {
  const axisMax = niceMax(Math.max(0, ...points.map((p) => p.financed)), STEPS);
  const lineMax = niceMax(
    Math.max(0, ...points.map((p) => p.intensity ?? 0)),
    STEPS,
  );

  // Columns are spread with space-between, so column i's centre sits at
  // i / (n - 1) of the strip once it is inset by half a column on each side.
  const last = Math.max(points.length - 1, 1);
  const linePoints = points.map((p, i) =>
    p.intensity === null
      ? null
      : {
          year: p.year,
          intensity: p.intensity,
          x: (i / last) * 100,
          y: 100 - (p.intensity / lineMax) * 100,
        },
  );
  // A year without outstanding has no intensity: break the line there.
  const segments = linePoints.reduce<XY[][]>(
    (acc, p) => {
      if (p) acc[acc.length - 1].push(p);
      else if (acc[acc.length - 1].length) acc.push([]);
      return acc;
    },
    [[]],
  ).filter((s) => s.length > 0);
  const dots = linePoints.filter(
    (p): p is NonNullable<typeof p> => p !== null,
  );

  return (
    <Box display="flex" flexDirection="column" gap="12px">
      {/* Details 7475:15228 */}
      <Box position="relative" pb="24px">
        <Typography
          fontSize="12px"
          fontWeight={400}
          lineHeight={1}
          height={`${ROW}px`}
          color="var(--chart-axis)"
        >
          {unit}
        </Typography>
        <Box mt="16px" display="flex" flexDirection="column" gap={`${ROW_GAP}px`}>
          {Array.from({ length: TICKS }, (_, i) => (
            <Box
              key={i}
              height={`${ROW}px`}
              display="flex"
              alignItems="center"
              gap={`${LABEL_GAP}px`}
            >
              <Typography
                width={`${LABEL_WIDTH}px`}
                flexShrink={0}
                textAlign="right"
                fontSize="12px"
                fontWeight={400}
                lineHeight={1}
                color="var(--chart-axis)"
                noWrap
              >
                {formatNumber((axisMax * (STEPS - i)) / STEPS)}
              </Typography>
              <Box flex={1} height="1px" bgcolor="var(--chart-grid)" />
            </Box>
          ))}
        </Box>

        {/* Column_Chart 7475:15254 */}
        <Box
          position="absolute"
          left={`${BARS_LEFT}px`}
          right={0}
          top={0}
          height={`${STRIP}px`}
          display="flex"
          alignItems="flex-end"
          justifyContent="space-between"
        >
          {points.map((p) => (
            <Box
              key={p.year}
              width={`${COLUMN}px`}
              height="100%"
              flexShrink={0}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="flex-end"
              gap={`${X_LABEL_GAP}px`}
            >
              <Tooltip title={`${barLabel}: ${formatNumber(p.financed)}`}>
                <Box
                  width={`${BAR}px`}
                  height={`${(p.financed / axisMax) * PLOT}px`}
                  bgcolor="var(--chart-blue)"
                  borderRadius="4px 4px 0 0"
                />
              </Tooltip>
              <Typography
                width="100%"
                textAlign="center"
                fontSize="14px"
                fontWeight={500}
                lineHeight={`${X_LABEL}px`}
                color="var(--chart-axis)"
              >
                {p.year}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Intensity line 7475:15285 — the path is stretched to the plot; the
            dots are laid out separately so they stay round. */}
        {dots.length > 0 && (
          <Box
            position="absolute"
            left={`${BARS_LEFT + COLUMN / 2}px`}
            right={`${COLUMN / 2}px`}
            top={`${ZERO - PLOT}px`}
            height={`${PLOT}px`}
          >
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              style={{ display: "block", overflow: "visible", pointerEvents: "none" }}
            >
              {segments.map((s) => (
                <path
                  key={`${s[0].x}`}
                  d={smoothPath(s)}
                  fill="none"
                  stroke="var(--green)"
                  strokeWidth={2}
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              ))}
            </svg>
            {dots.map((p) => (
              <Tooltip
                key={p.year}
                title={`${lineLabel}: ${formatIntensity(p.intensity)}`}
              >
                <Box
                  position="absolute"
                  left={`${p.x}%`}
                  top={`${p.y}%`}
                  width={`${DOT}px`}
                  height={`${DOT}px`}
                  boxSizing="border-box"
                  borderRadius="50%"
                  bgcolor="var(--green)"
                  border="2px solid var(--white)"
                  sx={{ transform: "translate(-50%, -50%)" }}
                />
              </Tooltip>
            ))}
          </Box>
        )}
      </Box>

      {/* Legend 7475:15300 */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap="28px"
        py="2px"
        flexWrap="wrap"
      >
        <Box display="flex" alignItems="center" gap="8px">
          <Box width="12px" height="12px" borderRadius="2px" bgcolor="var(--chart-blue)" />
          <Typography fontSize="12px" fontWeight={600} lineHeight="16px" color="var(--dark)">
            {barLabel}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap="8px">
          {/* Legend OB swatch 7475:15308: 24×2 line through a 10px dot */}
          <Box width="24px" height="12px" position="relative" display="flex" alignItems="center">
            <Box width="24px" height="2px" bgcolor="var(--green)" />
            <Box
              position="absolute"
              left="7px"
              top="1px"
              width="10px"
              height="10px"
              borderRadius="50%"
              bgcolor="var(--green)"
            />
          </Box>
          <Typography fontSize="12px" fontWeight={600} lineHeight="16px" color="var(--dark)">
            {lineLabel}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default EmissionTrendChart;
